import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users as UsersIcon,
  Search,
  Loader2,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Hash,
  Calendar,
  ArrowUpRight,
  Trash2,
  FileUp,
  CheckCircle2,
  Paperclip,
  LayoutGrid,
  Rows3,
  ArrowRightCircle,
  Send,
  BellRing,
  GitCompare,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ApplicantsKanban from "@/components/applicants/ApplicantsKanban";
import FilterPresets from "@/components/applicants/FilterPresets";
import BackgroundCheckModal from "@/components/BackgroundCheckModal";
import BackgroundCheckBadge from "@/components/BackgroundCheckBadge";

const STATUS_META = {
  new: {
    label: "New",
    className: "border-blue-300 bg-blue-50 text-blue-700",
  },
  awaiting_response: {
    label: "Awaiting Response",
    className: "border-amber-300 bg-amber-50 text-amber-800",
  },
  interview_scheduled: {
    label: "Interview Scheduled",
    className: "border-indigo-300 bg-indigo-50 text-indigo-700",
  },
  hired: {
    label: "Hired",
    className: "border-emerald-300 bg-emerald-50 text-emerald-800",
  },
  not_hired: {
    label: "Not Hired",
    className: "border-rose-300 bg-rose-50 text-rose-800",
  },
  not_interested: {
    label: "Not Interested",
    className: "border-zinc-300 bg-zinc-100 text-zinc-700",
  },
};

const formatReceived = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate()
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

export default function Applicants() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("received_desc");
  const [savingId, setSavingId] = useState(null);
  const [requestingId, setRequestingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bgModalRow, setBgModalRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [remindResult, setRemindResult] = useState(null);
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "table";
    return localStorage.getItem("hireloop_applicants_view") || "table";
  });
  const [sweeping, setSweeping] = useState(false);

  const setViewPersist = (v) => {
    setView(v);
    try {
      localStorage.setItem("hireloop_applicants_view", v);
    } catch {
      /* ignore quota / privacy errors */
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applicants");
      setApplicants(data.applicants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load applicants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, newStatus) => {
    setSavingId(id);
    // Optimistic update
    setApplicants((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, application_status: newStatus } : a
      )
    );
    try {
      await api.patch(`/applicants/${id}/status`, { status: newStatus });
      toast.success(`Status set to ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
      load(); // re-sync on failure
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Normalize legacy/alias sources so "uploaded", "upload", and "recruiter_upload"
    // all collapse into the same bucket.
    const normalizeSource = (s) => {
      const v = (s || "uploaded").toLowerCase();
      if (v === "upload" || v === "recruiter_upload") return "uploaded";
      return v;
    };
    const base = applicants.filter((a) => {
      if (filterStatus !== "all" && a.application_status !== filterStatus)
        return false;
      if (filterSource !== "all" && normalizeSource(a.source) !== filterSource)
        return false;
      if (!term) return true;
      return (
        (a.candidate_name || "").toLowerCase().includes(term) ||
        (a.candidate_email || "").toLowerCase().includes(term) ||
        (a.candidate_phone || "").toLowerCase().includes(term) ||
        (a.application_ref || "").toLowerCase().includes(term) ||
        (a.position_applied || "").toLowerCase().includes(term)
      );
    });
    // Sort
    const dateValue = (v) => (v ? new Date(v).getTime() : 0);
    const sorted = [...base];
    switch (sortBy) {
      case "opened_desc":
        sorted.sort(
          (a, b) => dateValue(b.email_opened_at) - dateValue(a.email_opened_at)
        );
        break;
      case "clicked_desc":
        sorted.sort(
          (a, b) => dateValue(b.email_clicked_at) - dateValue(a.email_clicked_at)
        );
        break;
      case "score_desc":
        sorted.sort((a, b) => (b.score || 0) - (a.score || 0));
        break;
      case "received_asc":
        sorted.sort(
          (a, b) => dateValue(a.received_at) - dateValue(b.received_at)
        );
        break;
      case "received_desc":
      default:
        sorted.sort(
          (a, b) => dateValue(b.received_at) - dateValue(a.received_at)
        );
        break;
    }
    return sorted;
  }, [applicants, filterStatus, filterSource, search, sortBy]);

  const counts = useMemo(() => {
    const c = { all: applicants.length };
    Object.keys(STATUS_META).forEach((k) => (c[k] = 0));
    applicants.forEach((a) => {
      c[a.application_status] = (c[a.application_status] || 0) + 1;
    });
    return c;
  }, [applicants]);

  // Per-source counts for the source filter dropdown — surface only sources that
  // actually exist on the current applicant set so we don't render empty options.
  const sourceCounts = useMemo(() => {
    const c = {};
    applicants.forEach((a) => {
      let s = (a.source || "uploaded").toLowerCase();
      if (s === "upload" || s === "recruiter_upload") s = "uploaded";
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [applicants]);

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleManySelected = (ids, shouldSelect) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (shouldSelect) next.add(id);
        else next.delete(id);
      });
      return next;
    });

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const someFilteredSelected =
    filtered.some((a) => selected.has(a.id)) && !allFilteredSelected;

  const toggleAllFiltered = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((a) => next.delete(a.id));
      } else {
        filtered.forEach((a) => next.add(a.id));
      }
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  const requestResume = async (id) => {
    setRequestingId(id);
    try {
      const { data } = await api.post(`/applicants/${id}/request-resume`);
      toast.success(`Résumé request emailed to ${data.sent_to}`);
      // Update in-place so the UI reflects the new resume_requested_at without a reload.
      setApplicants((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, resume_requested_at: new Date().toISOString() }
            : a
        )
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send résumé request"
      );
    } finally {
      setRequestingId(null);
    }
  };

  const requestBgCheckData = async (id) => {
    setRequestingId(id);
    try {
      const { data } = await api.post(
        `/applicants/${id}/request-bgcheck-data`
      );
      toast.success(`BG-check info form emailed to ${data.sent_to}`);
      setApplicants((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                bgcheck_data_requested_at: new Date().toISOString(),
                bgcheck_data_reminder_paused: false,
                bgcheck_data_needs_attention: false,
              }
            : a
        )
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send BG-check info request"
      );
    } finally {
      setRequestingId(null);
    }
  };

  const toggleBgcheckReminderPause = async (id) => {
    try {
      const { data } = await api.post(
        `/applicants/${id}/toggle-bgcheck-reminder`
      );
      setApplicants((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, bgcheck_data_reminder_paused: !!data.paused }
            : a
        )
      );
      toast.success(
        data.paused
          ? "Auto-reminder paused for this candidate"
          : "Auto-reminder re-enabled"
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not toggle the reminder"
      );
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/applicants/bulk-delete", {
        resume_ids: ids,
      });
      toast.success(
        `Deleted ${data.deleted} applicant${data.deleted === 1 ? "" : "s"}` +
          (data.storage_failed > 0
            ? ` · ${data.storage_failed} file${data.storage_failed === 1 ? "" : "s"} could not be removed from storage`
            : "")
      );
      setApplicants((prev) => prev.filter((a) => !selected.has(a.id)));
      clearSelection();
      setConfirmOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const sweepNewToAwaiting = async () => {
    if (counts.new === 0) return;
    setSweeping(true);
    // Optimistic — flip every "new" row to "awaiting_response" locally.
    setApplicants((prev) =>
      prev.map((a) =>
        (a.application_status || "new") === "new"
          ? { ...a, application_status: "awaiting_response" }
          : a
      )
    );
    try {
      const { data } = await api.post("/applicants/bulk-status", {
        resume_ids: [],
        from_status: "new",
        status: "awaiting_response",
      });
      toast.success(
        `Moved ${data.updated} applicant${data.updated === 1 ? "" : "s"} to Awaiting Response`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk status update failed");
      load(); // re-sync
    } finally {
      setSweeping(false);
    }
  };

  const bulkInvite = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setInviting(true);
    try {
      const { data } = await api.post("/applicants/bulk-invite", {
        resume_ids: ids,
        send_emails: true,
        invite_link_base: window.location.origin,
      });
      const { invited, emailed, skipped, results } = data;
      const skippedRows = (results || []).filter((r) => r.skipped);
      setInviteResult({
        invited,
        emailed,
        skipped,
        skippedRows,
      });
      // Patch local state — mirror server's status flip + interview_id mint.
      setApplicants((prev) =>
        prev.map((a) => {
          const r = (results || []).find((x) => x.resume_id === a.id);
          if (!r || r.skipped) return a;
          return {
            ...a,
            application_status: "awaiting_response",
            interview_id: r.interview_id,
          };
        })
      );
      clearSelection();
      toast.success(
        `Invited ${invited} candidate${invited === 1 ? "" : "s"}` +
          (emailed > 0 ? ` · ${emailed} email${emailed === 1 ? "" : "s"} sent` : "") +
          (skipped > 0 ? ` · ${skipped} skipped` : "")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally {
      setInviting(false);
    }
  };

  const bulkRemind = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setReminding(true);
    try {
      const { data } = await api.post("/applicants/bulk-remind", {
        resume_ids: ids,
        invite_link_base: window.location.origin,
      });
      const { reminded, skipped, results } = data;
      const skippedRows = (results || []).filter((r) => r.skipped);
      setRemindResult({ reminded, skipped, skippedRows });
      clearSelection();
      toast.success(
        `Reminded ${reminded} candidate${reminded === 1 ? "" : "s"}` +
          (skipped > 0 ? ` · ${skipped} skipped` : "")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk reminder failed");
    } finally {
      setReminding(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">
            Applicants
          </div>
          <h1 className="mt-2 font-heading text-3xl font-semibold lg:text-4xl">
            All applicants
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Every candidate who applied across every job — auto-extracted
            contact info, reference number, and pipeline status.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <UsersIcon className="h-4 w-4" />
          <span data-testid="applicants-total-count" className="font-semibold">
            {counts.all}
          </span>
          <span>total</span>
        </div>
      </div>

      <FilterPresets
        filterStatus={filterStatus}
        filterSource={filterSource}
        sortBy={sortBy}
        search={search}
        onApply={(f) => {
          setFilterStatus(f.filterStatus ?? "all");
          setFilterSource(f.filterSource ?? "all");
          setSortBy(f.sortBy ?? "received_desc");
          setSearch(f.search ?? "");
        }}
      />

      {/* Toolbar */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="applicants-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, ref, or position…"
            className="pl-9"
          />
        </div>
        <div
          data-testid="applicants-status-filter"
          className="inline-flex flex-wrap items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1"
        >
          {[
            ["all", "All", "bg-slate-200 text-slate-900"],
            ["new", "New", "bg-blue-100 text-blue-700"],
            ["awaiting_response", "Awaiting", "bg-amber-100 text-amber-700"],
            ["interview_scheduled", "Scheduled", "bg-indigo-100 text-indigo-700"],
            ["hired", "Hired", "bg-emerald-100 text-emerald-700"],
            ["not_hired", "Not hired", "bg-rose-100 text-rose-700"],
            ["not_interested", "Not interested", "bg-zinc-200 text-zinc-700"],
          ].map(([k, label, activeTint]) => {
            const isActive = filterStatus === k;
            return (
              <button
                key={k}
                type="button"
                data-testid={`filter-${k}`}
                onClick={() => setFilterStatus(k)}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] transition ${
                  isActive
                    ? `${activeTint} shadow-sm`
                    : "bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                {label}
                <span className="ml-2 text-[10px] opacity-70">
                  {k === "all" ? counts.all : counts[k] || 0}
                </span>
              </button>
            );
          })}
        </div>
        <select
          data-testid="applicants-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="rounded-sm border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700"
          title="Sort applicants"
        >
          <option value="received_desc">Newest first</option>
          <option value="received_asc">Oldest first</option>
          <option value="score_desc">Highest score</option>
          <option value="opened_desc">Most recently opened</option>
          <option value="clicked_desc">Most recently clicked</option>
        </select>
        <select
          data-testid="applicants-source-filter"
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="rounded-sm border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700"
          title="Filter by application source"
        >
          <option value="all">All sources ({applicants.length})</option>
          {[
            ["online_application", "Online App"],
            ["online_application_with_resume", "Form + Résumé"],
            ["public_apply", "Public Apply"],
            ["uploaded", "Uploaded"],
            ["careers_page", "Careers Page"],
            ["email_forward", "Email"],
          ]
            .filter(([k]) => (sourceCounts[k] || 0) > 0)
            .map(([k, label]) => (
              <option key={k} value={k}>
                {label} ({sourceCounts[k]})
              </option>
            ))}
          {/* Surface any uncommon/legacy sources we don't have a label for */}
          {Object.keys(sourceCounts)
            .filter(
              (k) =>
                ![
                  "online_application",
                  "online_application_with_resume",
                  "public_apply",
                  "uploaded",
                  "careers_page",
                  "email_forward",
                ].includes(k)
            )
            .map((k) => (
              <option key={k} value={k}>
                {k} ({sourceCounts[k]})
              </option>
            ))}
        </select>
        <div
          data-testid="applicants-view-toggle"
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1"
        >
          <button
            type="button"
            data-testid="applicants-view-table"
            onClick={() => setViewPersist("table")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] transition ${
              view === "table"
                ? "bg-blue-100 text-blue-700 shadow-sm"
                : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Rows3 className="h-3 w-3" />
            Table
          </button>
          <button
            type="button"
            data-testid="applicants-view-kanban"
            onClick={() => setViewPersist("kanban")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] transition ${
              view === "kanban"
                ? "bg-blue-100 text-blue-700 shadow-sm"
                : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            <LayoutGrid className="h-3 w-3" />
            Kanban
          </button>
        </div>
      </div>

      {/* Quick-action bar: "Move all New → Awaiting Response" */}
      {counts.new > 0 && (
        <div
          data-testid="applicants-sweep-bar"
          className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="text-sm text-amber-900">
            You have{" "}
            <span data-testid="applicants-sweep-count" className="font-semibold">
              {counts.new}
            </span>{" "}
            applicant{counts.new === 1 ? "" : "s"} in <b>New</b>. Move them all to <b>Awaiting Response</b> in one click — no candidate emails are sent.
          </div>
          <Button
            data-testid="applicants-sweep-btn"
            size="sm"
            onClick={sweepNewToAwaiting}
            disabled={sweeping}
            className="bg-amber-600 text-white hover:bg-amber-500"
          >
            {sweeping ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRightCircle className="mr-2 h-3.5 w-3.5" />
            )}
            Move all New → Awaiting
          </Button>
        </div>
      )}

      {/* Bulk selection action bar (only when at least one row is selected) */}
      {selected.size > 0 && (
        <div
          data-testid="applicants-bulk-bar"
          className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-rose-200 bg-rose-50 px-4 py-3"
        >
          <div className="text-sm text-rose-900">
            <span
              data-testid="applicants-bulk-count"
              className="font-semibold"
            >
              {selected.size}
            </span>{" "}
            applicant{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="applicants-bulk-clear"
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="text-rose-900 hover:bg-rose-100"
            >
              Clear
            </Button>
            <Button
              data-testid="applicants-bulk-invite-btn"
              size="sm"
              onClick={bulkInvite}
              disabled={inviting}
              className="bg-blue-600 text-white hover:bg-blue-500"
            >
              {inviting ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="mr-2 h-3.5 w-3.5" />
              )}
              Invite to interview
            </Button>
            <Button
              data-testid="applicants-bulk-remind-btn"
              size="sm"
              onClick={bulkRemind}
              disabled={reminding}
              className="bg-violet-600 text-white hover:bg-violet-500"
            >
              {reminding ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <BellRing className="mr-2 h-3.5 w-3.5" />
              )}
              Send reminder
            </Button>
            <Button
              data-testid="applicants-bulk-compare-btn"
              size="sm"
              onClick={() => {
                const ids = Array.from(selected);
                if (ids.length < 2) {
                  toast.error("Pick at least 2 candidates to compare");
                  return;
                }
                if (ids.length > 4) {
                  toast.error("Compare up to 4 candidates at a time");
                  return;
                }
                window.location.href = `/compare?ids=${ids.join(",")}`;
              }}
              disabled={selected.size < 2 || selected.size > 4}
              className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-slate-300"
            >
              <GitCompare className="mr-2 h-3.5 w-3.5" />
              Compare ({selected.size})
            </Button>
            <Button
              data-testid="applicants-bulk-delete-btn"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              className="bg-rose-600 text-white hover:bg-rose-500"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete selected
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      {view === "kanban" ? (
        <ApplicantsKanban
          applicants={filtered}
          onStatusChange={updateStatus}
          selected={selected}
          onToggleSelect={toggleOne}
          onToggleManySelected={toggleManySelected}
        />
      ) : (
      <div className="mt-6 overflow-hidden border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table
            data-testid="applicants-table"
            className="w-full min-w-[1100px] border-collapse text-sm"
          >
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    data-testid="applicants-select-all"
                    checked={
                      allFilteredSelected
                        ? true
                        : someFilteredSelected
                        ? "indeterminate"
                        : false
                    }
                    onCheckedChange={toggleAllFiltered}
                    aria-label="Select all visible applicants"
                  />
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="inline-flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    Ref #
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Received
                  </span>
                </th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Email
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    Phone
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Address
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="inline-flex items-center gap-1">
                    <Briefcase className="h-3 w-3" />
                    Position applied
                  </span>
                </th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center text-slate-500">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    <div className="mt-2 text-xs">Loading applicants…</div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center text-slate-500">
                    {applicants.length === 0
                      ? "No applicants yet. Once résumés are uploaded or applications come in via the public careers page, they'll appear here."
                      : "No applicants match your current filter."}
                  </td>
                </tr>
              ) : (
                filtered.map((a, i) => {
                  const meta = STATUS_META[a.application_status] || STATUS_META.new;
                  return (
                    <tr
                      key={a.id}
                      data-testid={`applicant-row-${i}`}
                      className={`border-t border-slate-100 hover:bg-slate-50 ${
                        selected.has(a.id) ? "bg-rose-50/40" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          data-testid={`applicant-select-${i}`}
                          checked={selected.has(a.id)}
                          onCheckedChange={() => toggleOne(a.id)}
                          aria-label={`Select ${a.candidate_name || a.application_ref}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900 tabular-nums">
                        {a.application_ref || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 tabular-nums">
                        {formatReceived(a.received_at)}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        <div className="flex items-center gap-2">
                          <span>{a.candidate_name || "—"}</span>
                          {a.source === "online_application_with_resume" && (
                            <span
                              data-testid={`applicant-attached-${i}`}
                              title="Candidate attached a résumé via the tokenized link"
                              className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-emerald-400 bg-emerald-50 text-emerald-700"
                            >
                              <Paperclip className="h-2.5 w-2.5" />
                            </span>
                          )}
                          <span
                            data-testid={`applicant-source-${i}`}
                            title={
                              a.source === "online_application"
                                ? "Submitted via online application form"
                                : a.source === "online_application_with_resume"
                                ? "Online application + attached résumé"
                                : a.source === "public_apply"
                                ? "Uploaded a résumé via the public careers page"
                                : "Uploaded by recruiter"
                            }
                            className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${
                              a.source === "online_application"
                                ? "border-violet-300 bg-violet-50 text-violet-700"
                                : a.source === "online_application_with_resume"
                                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                : "border-slate-300 bg-slate-50 text-slate-600"
                            }`}
                          >
                            {a.source === "online_application"
                              ? "Form"
                              : a.source === "online_application_with_resume"
                              ? "Form + Résumé"
                              : "Résumé"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {a.candidate_email ? (
                          <a
                            href={`mailto:${a.candidate_email}`}
                            className="hover:underline"
                          >
                            {a.candidate_email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 tabular-nums">
                        {a.candidate_phone || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[220px] truncate" title={a.candidate_address || ""}>
                        {a.candidate_address || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-700">
                        {a.position_applied ? (
                          <Link
                            to={`/jobs/${a.job_id}`}
                            className="inline-flex items-center gap-1 hover:underline"
                          >
                            {a.position_applied}
                            <ArrowUpRight className="h-3 w-3 opacity-50" />
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={a.application_status || "new"}
                          onValueChange={(v) => updateStatus(a.id, v)}
                          disabled={savingId === a.id}
                        >
                          <SelectTrigger
                            data-testid={`applicant-status-${i}`}
                            className={`h-8 w-[180px] rounded-sm border text-[11px] font-semibold uppercase tracking-[0.14em] ${meta.className}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_META).map(([k, v]) => (
                              <SelectItem key={k} value={k} className="text-xs">
                                {v.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {a.background_check_status && (
                            <BackgroundCheckBadge
                              status={a.background_check_status}
                              assessment={a.background_check_assessment}
                            />
                          )}
                          {!a.background_check_status && (
                            <>
                              {a.bgcheck_data_received_at ? (
                                <span
                                  data-testid={`applicant-bg-data-ready-${i}`}
                                  title={`Legal name & work state confirmed by candidate on ${formatReceived(a.bgcheck_data_received_at)}`}
                                  className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                  BG info ready
                                </span>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    data-testid={`applicant-request-bg-info-${i}`}
                                    onClick={() => requestBgCheckData(a.id)}
                                    disabled={requestingId === a.id}
                                    title={
                                      a.bgcheck_data_requested_at
                                        ? `BG-check info already requested ${formatReceived(a.bgcheck_data_requested_at)} — click to send again`
                                        : "Email the candidate a short form asking for their legal name and work state"
                                    }
                                    className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider hover:bg-amber-50 disabled:opacity-50 ${
                                      a.bgcheck_data_needs_attention
                                        ? "border-rose-300 bg-rose-50 text-rose-700"
                                        : "border-amber-200 bg-white text-amber-700"
                                    }`}
                                  >
                                    <Send className="h-3 w-3" />
                                    {a.bgcheck_data_needs_attention
                                      ? "BG info overdue"
                                      : a.bgcheck_data_requested_at
                                        ? "Resend BG info"
                                        : "Request BG info"}
                                  </button>
                                  {a.bgcheck_data_requested_at && (
                                    <button
                                      type="button"
                                      data-testid={`applicant-pause-bg-reminder-${i}`}
                                      onClick={() =>
                                        toggleBgcheckReminderPause(a.id)
                                      }
                                      title={
                                        a.bgcheck_data_reminder_paused
                                          ? "Auto-reminder is paused — click to re-enable"
                                          : "Pause the 72h auto-reminder for this candidate (I'll follow up manually)"
                                      }
                                      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                        a.bgcheck_data_reminder_paused
                                          ? "border-slate-300 bg-slate-100 text-slate-600"
                                          : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                                      }`}
                                    >
                                      {a.bgcheck_data_reminder_paused
                                        ? "Reminder paused"
                                        : "Pause reminder"}
                                    </button>
                                  )}
                                </>
                              )}
                              <button
                                type="button"
                                data-testid={`applicant-bg-btn-${i}`}
                                onClick={() => setBgModalRow(a)}
                                title="Order a Checkr background check"
                                className="inline-flex items-center gap-1 border border-emerald-200 bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:bg-emerald-50"
                              >
                                <ShieldCheck className="h-3 w-3" />
                                BG check
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {a.source === "online_application" && (
                          <button
                            type="button"
                            data-testid={`applicant-request-resume-${i}`}
                            onClick={() => requestResume(a.id)}
                            disabled={requestingId === a.id}
                            title={
                              a.resume_requested_at
                                ? `Résumé already requested ${formatReceived(a.resume_requested_at)} — click to send again`
                                : "Ask the candidate to attach a résumé"
                            }
                            className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] transition disabled:opacity-50 ${
                              a.resume_requested_at
                                ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                            }`}
                          >
                            {requestingId === a.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : a.resume_requested_at ? (
                              <CheckCircle2 className="h-3 w-3" />
                            ) : (
                              <FileUp className="h-3 w-3" />
                            )}
                            {a.resume_requested_at ? "Requested" : "Request résumé"}
                          </button>
                        )}
                        {savingId === a.id && (
                          <Loader2 className="ml-auto h-3 w-3 animate-spin text-slate-400" />
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="applicants-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selected.size} applicant{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the candidate record, the uploaded
              résumé file in object storage, and any linked AI-interview
              session. <span className="font-semibold text-rose-700">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="applicants-bulk-delete-cancel"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="applicants-bulk-delete-confirm"
              onClick={(e) => {
                e.preventDefault();
                bulkDelete();
              }}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-500"
            >
              {deleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete {selected.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk invite results */}
      <AlertDialog
        open={!!inviteResult}
        onOpenChange={(o) => !o && setInviteResult(null)}
      >
        <AlertDialogContent data-testid="applicants-bulk-invite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {inviteResult?.invited === 0
                ? "No invites sent"
                : `Invited ${inviteResult?.invited} candidate${inviteResult?.invited === 1 ? "" : "s"}`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                {inviteResult && inviteResult.invited > 0 && (
                  <div>
                    <span className="font-semibold text-emerald-700">
                      {inviteResult.emailed}
                    </span>{" "}
                    email{inviteResult.emailed === 1 ? "" : "s"} sent successfully. Each candidate received a personalised interview link and the applicant's status was flipped to <b>Awaiting Response</b>.
                  </div>
                )}
                {inviteResult && inviteResult.skippedRows.length > 0 && (
                  <div>
                    <div className="font-semibold text-amber-800">
                      {inviteResult.skippedRows.length} skipped
                    </div>
                    <ul
                      data-testid="bulk-invite-skipped-list"
                      className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded-sm border border-slate-200 bg-slate-50 p-2 text-xs"
                    >
                      {inviteResult.skippedRows.map((r) => (
                        <li key={r.resume_id} className="flex justify-between gap-3">
                          <span className="font-medium text-slate-800">
                            {r.candidate_name || r.resume_id}
                          </span>
                          <span className="text-slate-500">
                            {r.skipped === "no_email"
                              ? "No email on file"
                              : r.skipped === "job_missing"
                              ? "Job missing"
                              : r.skipped === "not_found"
                              ? "Not found / not yours"
                              : r.skipped}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              data-testid="bulk-invite-dialog-ok"
              onClick={() => setInviteResult(null)}
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk reminder results */}
      <AlertDialog
        open={!!remindResult}
        onOpenChange={(o) => !o && setRemindResult(null)}
      >
        <AlertDialogContent data-testid="applicants-bulk-remind-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {remindResult?.reminded === 0
                ? "No reminders sent"
                : `Reminded ${remindResult?.reminded} candidate${remindResult?.reminded === 1 ? "" : "s"}`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                {remindResult && remindResult.reminded > 0 && (
                  <div>
                    <span className="font-semibold text-violet-700">
                      {remindResult.reminded}
                    </span>{" "}
                    polite nudge email{remindResult.reminded === 1 ? "" : "s"} dispatched. Each candidate's reminder counter was bumped (we still respect your <b>max-reminders</b> ceiling on the next manual or auto-send).
                  </div>
                )}
                {remindResult && remindResult.skippedRows.length > 0 && (
                  <div>
                    <div className="font-semibold text-amber-800">
                      {remindResult.skippedRows.length} skipped
                    </div>
                    <ul
                      data-testid="bulk-remind-skipped-list"
                      className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded-sm border border-slate-200 bg-slate-50 p-2 text-xs"
                    >
                      {remindResult.skippedRows.map((r) => (
                        <li key={r.resume_id} className="flex justify-between gap-3">
                          <span className="font-medium text-slate-800">
                            {r.candidate_name || r.resume_id}
                          </span>
                          <span className="text-slate-500">
                            {r.skipped === "no_email"
                              ? "No email on file"
                              : r.skipped === "no_invite"
                              ? "Not invited yet"
                              : r.skipped === "ceiling_reached"
                              ? `Ceiling reached (${r.reminders_sent}/${r.max_reminders})`
                              : r.skipped === "already_engaged"
                              ? "Already engaged"
                              : r.skipped === "interview_missing"
                              ? "Interview missing"
                              : r.skipped === "send_failed"
                              ? `Send failed${r.error ? ` — ${r.error}` : ""}`
                              : r.skipped === "not_found"
                              ? "Not found / not yours"
                              : r.skipped}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              data-testid="bulk-remind-dialog-ok"
              onClick={() => setRemindResult(null)}
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BackgroundCheckModal
        open={!!bgModalRow}
        onOpenChange={(o) => !o && setBgModalRow(null)}
        resumeId={bgModalRow?.id}
        candidateName={bgModalRow?.candidate_name || ""}
        candidateEmail={bgModalRow?.candidate_email || ""}
        onOrdered={(bg) => {
          setApplicants((prev) =>
            prev.map((a) =>
              a.id === bg.resume_id
                ? {
                    ...a,
                    background_check_status: bg.status,
                    background_check_id: bg.id,
                  }
                : a
            )
          );
        }}
      />
    </main>
  );
}
